import React, { useState, useMemo } from 'react';
import {
    Briefcase, Building2, Database, TrendingUp, Users, CheckCircle,
    AlertCircle, XCircle, Activity, Calendar, Loader,
    ChevronUp, ChevronDown, ChevronsUpDown, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
    ResponsiveContainer,
    AreaChart, Area,
    LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { useTheme } from '../../hooks/useTheme';

const timeframeOptions = [
    { value: 'daily', label: 'Daily (24h)' },
    { value: 'weekly', label: 'Weekly (7d)' },
    { value: 'biweekly', label: 'Bi-Weekly (14d)' },
    { value: 'monthly', label: 'Monthly (30d)' },
    { value: 'yearly', label: 'Yearly (365d)' },
    { value: 'overall', label: 'All Time' }
];

const SERIES = [
    { key: 'added',      name: 'Added',      color: '#10b981' },
    { key: 'duplicates', name: 'Duplicates', color: '#3b82f6' },
    { key: 'failed',     name: 'Failed',     color: '#ef4444' },
    { key: 'skipped',    name: 'Skipped',    color: '#f59e0b' },
    { key: 'retired',    name: 'Retired',    color: '#8b5cf6' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function SortIcon({ isFailedView = true, column, sortKey, sortDir }) {
    const displayColor = isFailedView ? 'red' : 'primary'
    if (sortKey !== column) return <ChevronsUpDown size={14} className="text-slate-400 ml-1 inline" />;
    return sortDir === 'asc'
        ? <ChevronUp size={14} className={`text-${displayColor}-600 ml-1 inline`} />
        : <ChevronDown size={14} className={`text-${displayColor}-600 ml-1 inline`} />;
}

function ExtractStatusGrid({ failures, successes, timeframeLabel }) {
    // this component default mode is to display failures data
    const { isDarkMode } = useTheme();
    const [view, setView] = useState('failed'); // 'failed' | 'successful'
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('failure_count');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const isFailedView = view === 'failed';
    const records = isFailedView ? failures : successes;

    const accent = isFailedView
        ? { badgeBg: 'bg-red-100', badgeText: 'text-red-700', hoverRow: isDarkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50', activeBtn: 'bg-red-600 text-white' }
        : { badgeBg: 'bg-primary-100', badgeText: 'text-primary-700', hoverRow: isDarkMode ? 'hover:bg-primary-900/20' : 'hover:bg-primary-50', activeBtn: 'bg-primary-600 text-white' };

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'failure_count' ? 'desc' : 'asc');
        }
        setPage(1);
    };

    const handleViewChange = (v) => {
        setView(v);
        setSortKey(v === 'failed' ? 'failure_count' : 'success_count')
        setPage(1);
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return (records || []).filter(f =>
            !q || f.company_name?.toLowerCase().includes(q) || f.last_error?.toLowerCase().includes(q)
        );
    }, [records, search]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            let av = a[sortKey], bv = b[sortKey];
            if (['last_failure_time', 'last_run_time'].includes(sortKey)) {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
            }
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filtered, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

    const segBtnClass = (active) => `px-3 py-1 rounded-md text-sm font-medium transition-colors ${
        active
            ? accent.activeBtn
            : isDarkMode ? 'text-slate-300 hover:bg-slate-600' : 'text-slate-600 hover:bg-slate-200'
    }`;

    const thClass = `py-2.5 px-3 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer select-none transition-colors whitespace-nowrap border ${
        isDarkMode
            ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
    }`;
    const tdClass = `py-2.5 px-3 text-sm border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`;

    return (
        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                {isFailedView
                    ? <XCircle className="text-red-600 shrink-0" size={22} />
                    : <CheckCircle className="text-primary-600 shrink-0" size={22} />
                }
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                    Extract Status ({timeframeLabel})
                </h2>
                {/* View Toggle */}
                <div className={`inline-flex rounded-lg border p-0.5 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-slate-100'}`}>
                    <button onClick={() => handleViewChange('failed')} className={segBtnClass(isFailedView)}>
                        Failed
                    </button>
                    <button onClick={() => handleViewChange('successful')} className={segBtnClass(!isFailedView)}>
                        Successful
                    </button>
                </div>
                {records && records.length > 0 && (
                    <span className={`px-2.5 py-0.5 ${accent.badgeBg} ${accent.badgeText} rounded-full text-sm font-semibold`}>
                        {records.length} {records.length === 1 ? 'record' : 'records'}
                    </span>
                )}
                {/* Search */}
                <div className="ml-auto relative">
                    <Search size={15} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-black'}`} />
                    <input
                        type="text"
                        placeholder="Search company or error…"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className={`pl-8 pr-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-400 outline-none w-56 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`}
                    />
                </div>
            </div>

            {/* Empty states */}
            {(!records || records.length === 0) ? (
                <div className={`flex items-center justify-center h-24 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {isFailedView ? 'No extract failures for this timeframe.' : 'No successful extracts for this timeframe.'}
                </div>
            ) : filtered.length === 0 ? (
                <div className={`flex items-center justify-center h-24 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    No results match your search.
                </div>
            ) : (
                <>
                    <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr>
                                    <th className={thClass} style={{width:'25%'}} onClick={() => handleSort('company_name')}>
                                        Company <SortIcon isFailedView={isFailedView} column="company_name" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thClass} style={{width:'15%'}} onClick={() => handleSort('search_text')}>
                                        Search Text <SortIcon isFailedView={isFailedView} column="search_text" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={`${thClass} text-center`} style={{width:'8%'}} onClick={() => handleSort('max_pages')}>
                                        Max Pages <SortIcon isFailedView={isFailedView} column="max_pages" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    {isFailedView ?
                                        <th className={`${thClass} text-center`} style={{width:'8%'}} onClick={() => handleSort('failure_count')}>
                                            # Failures <SortIcon isFailedView={isFailedView} column="failure_count" sortKey={sortKey} sortDir={sortDir} />
                                        </th> :
                                        <th className={`${thClass} text-center`} style={{width:'8%'}} onClick={() => handleSort('success_count')}>
                                            # Success <SortIcon isFailedView={isFailedView} column="success_count" sortKey={sortKey} sortDir={sortDir} />
                                        </th>
                                    }
                                    <th className={`${thClass} text-center`} style={{width:'7%'}} onClick={() => handleSort('jobs_found')}>
                                        Found <SortIcon isFailedView={isFailedView} column="jobs_found" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    {isFailedView ?
                                        <th className={`${thClass} text-center`} style={{width:'7%'}} onClick={() => handleSort('jobs_saved')}>
                                            Saved <SortIcon isFailedView={isFailedView} column="jobs_saved" sortKey={sortKey} sortDir={sortDir} />
                                        </th> :
                                        <th className={`${thClass} text-center`} style={{width:'7%'}} onClick={() => handleSort('jobs_failed')}>
                                            Failed <SortIcon isFailedView={isFailedView} column="jobs_failed" sortKey={sortKey} sortDir={sortDir} />
                                        </th>
                                    }
                                    <th className={`${thClass} text-center`} style={{width:'7%'}} onClick={() => handleSort('jobs_duplicates')}>
                                        Duped <SortIcon isFailedView={isFailedView} column="jobs_duplicates" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    {isFailedView ?
                                        <th className={thClass} style={{width:'14%'}} onClick={() => handleSort('last_failure_time')}>
                                            Latest Fail Date <SortIcon isFailedView={isFailedView} column="last_failure_time" sortKey={sortKey} sortDir={sortDir} />
                                        </th> :
                                        <th className={thClass} style={{width:'14%'}} onClick={() => handleSort('last_run_time')}>
                                            Latest Run Date <SortIcon isFailedView={isFailedView} column="last_run_time" sortKey={sortKey} sortDir={sortDir} />
                                        </th>
                                    }
                                    {isFailedView &&
                                        <th className={thClass} style={{width:'22%'}}>
                                            Last Error
                                        </th>
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((company, index) => {
                                    const recordDate = isFailedView ? company.last_failure_time : company.last_run_time
                                    const displayDate = recordDate
                                        ? new Date(recordDate).toLocaleString(undefined, {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                          })
                                        : '—';
                                    return (
                                        <tr
                                            key={index}
                                            className={`transition-colors ${
                                                isDarkMode
                                                    ? `${index % 2 === 0 ? 'bg-slate-800' : 'bg-slate-750'} ${accent.hoverRow}`
                                                    : `${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${accent.hoverRow}`
                                            }`}
                                        >
                                            <td className={`${tdClass} font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{company.company_name}</td>
                                            <td className={`${tdClass} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                <span className="block truncate" title={company.search_text || ''}>
                                                    {company.search_text || '—'}
                                                </span>
                                            </td>
                                            <td className={`${tdClass} text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                {company.max_pages ?? '—'}
                                            </td>
                                            <td className={`${tdClass} text-center`}>
                                                <span className={`px-2.5 py-0.5 ${accent.badgeBg} ${accent.badgeText} rounded-full font-semibold text-xs`}>
                                                    {isFailedView ? company.failure_count : company.success_count}
                                                </span>
                                            </td>
                                            <td className={`${tdClass} text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                {company.jobs_found ?? '—'}
                                            </td>
                                            <td className={`${tdClass} text-center text-${isFailedView ? 'primary' : 'red'}-700 font-medium`}>
                                                {isFailedView ? company.jobs_saved : company.jobs_failed ?? '—'}
                                            </td>
                                            <td className={`${tdClass} text-center text-blue-700`}>
                                                {company.jobs_duplicates ?? '—'}
                                            </td>
                                            <td className={`${tdClass} whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{displayDate}</td>
                                            {isFailedView && 
                                                <td className={`${tdClass} max-w-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    <span className="block truncate" title={company.last_error || ''}>
                                                        {company.last_error || '—'}
                                                    </span>
                                                </td>
                                            }
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className={`flex items-center justify-between mt-3 text-sm flex-wrap gap-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        <div className="flex items-center gap-2">
                            <span>Rows per page:</span>
                            <select
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                className={`border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-gray-400 outline-none ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300'}`}
                            >
                                {PAGE_SIZE_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className={`mr-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sorted.length)} of {sorted.length}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className={`p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => {
                                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, i) =>
                                    p === '...'
                                        ? <span key={`ellipsis-${i}`} className="px-1">…</span>
                                        : <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            className={`w-7 h-7 rounded text-xs font-medium ${
                                                page === p
                                                    ? 'bg-gray-600 text-white'
                                                    : isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-700'
                                            }`}
                                        >{p}</button>
                                )
                            }
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className={`p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function ActivityChart({ data, timeframe }) {
    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">
                No scraping activity data for this timeframe.
            </div>
        );
    }

    // For "daily" (hourly buckets) and "overall" (sparse days): use LineChart
    if (timeframe === 'daily' || timeframe === 'overall') {
        const activeData = timeframe === 'overall'
            ? data.filter(d => d.added > 0 || d.duplicates > 0 || d.failed > 0 || d.skipped > 0)
            : data;

        if (activeData.length === 0) {
            return (
                <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">
                    No scraping activity data for this timeframe.
                </div>
            );
        }

        return (
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    {SERIES.map(s => (
                        <Line
                            key={s.key}
                            type="linear"
                            dataKey={s.key}
                            stroke={s.color}
                            name={s.name}
                            dot={{ r: 3 }}
                            strokeWidth={2}
                            connectNulls={false}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                {SERIES.map(s => (
                    <Area
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        stroke={s.color}
                        fill={s.color}
                        fillOpacity={0.15}
                        name={s.name}
                        strokeWidth={2}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
}

export default function StatisticsTab({
    stats,
    statsLoading,
    scrapeHistory,
    timeframe,
    setTimeframe,
    onRefresh,
}) {
    const { isDarkMode } = useTheme();

    const timeframeLabel = timeframe === 'overall'
        ? 'All Time'
        : timeframeOptions.find(o => o.value === timeframe)?.label;

    return (
        <div className="space-y-6">
            {/* Timeframe Selector */}
            <div className={`rounded-2xl border shadow-sm p-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-4">
                    <Calendar size={20} className={isDarkMode ? 'text-slate-400' : 'text-slate-600'} />
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Statistics Timeframe:</label>
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`}
                    >
                        {timeframeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={onRefresh}
                        disabled={statsLoading}
                        className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                        <Activity size={16} className={statsLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {statsLoading ? (
                <div className={`rounded-2xl border shadow-sm p-12 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <Loader className="animate-spin mx-auto text-primary-600 mb-4" size={48} />
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading statistics...</p>
                </div>
            ) : stats && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="rounded-2xl p-6 text-white shadow-lg" style={{ background: 'linear-gradient(to bottom right, #10b981, #059669)' }}>
                            <div className="flex items-center justify-between mb-2">
                                <Briefcase size={32} className="opacity-80" />
                                <TrendingUp size={20} />
                            </div>
                            <div className="text-3xl font-bold mb-1">{stats.total_jobs?.toLocaleString()}</div>
                            <div className="text-primary-100">Total Job Listings</div>
                        </div>

                        <div className="rounded-2xl p-6 text-white shadow-lg" style={{ background: 'linear-gradient(to bottom right, #3b82f6, #2563eb)' }}>
                            <div className="flex items-center justify-between mb-2">
                                <Building2 size={32} className="opacity-80" />
                                <Users size={20} />
                            </div>
                            <div className="text-3xl font-bold mb-1">{stats.total_employers?.toLocaleString()}</div>
                            <div className="text-blue-100">Active Employers</div>
                        </div>

                        <div className="rounded-2xl p-6 text-white shadow-lg" style={{ background: 'linear-gradient(to bottom right, #a855f7, #9333ea)' }}>
                            <div className="flex items-center justify-between mb-2">
                                <Database size={32} className="opacity-80" />
                                <CheckCircle size={20} />
                            </div>
                            <div className="text-3xl font-bold mb-1">{stats.employers_with_portal?.toLocaleString()}</div>
                            <div className="text-purple-100">With Workday Portals</div>
                        </div>
                    </div>

                    {/* Scraping Performance */}
                    {stats.scrape_stats && (
                        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                Extracting Performance ({timeframeLabel})
                            </h2>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                                <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="text-primary-600" size={20} />
                                        <span className="font-medium text-slate-700">Added</span>
                                    </div>
                                    <div className="text-2xl font-bold text-primary-700">
                                        {stats.scrape_stats.jobs_added?.toLocaleString() || 0}
                                    </div>
                                </div>

                                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="text-yellow-600" size={20} />
                                        <span className="font-medium text-slate-700">Skipped</span>
                                    </div>
                                    <div className="text-2xl font-bold text-yellow-700">
                                        {stats.scrape_stats.jobs_skipped?.toLocaleString() || 0}
                                    </div>
                                </div>

                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Activity className="text-blue-600" size={20} />
                                        <span className="font-medium text-slate-700">Duplicates</span>
                                    </div>
                                    <div className="text-2xl font-bold text-blue-700">
                                        {stats.scrape_stats.jobs_duplicates?.toLocaleString() || 0}
                                    </div>
                                </div>

                                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <XCircle className="text-red-600" size={20} />
                                        <span className="font-medium text-slate-700">Failed</span>
                                    </div>
                                    <div className="text-2xl font-bold text-red-700">
                                        {stats.scrape_stats.jobs_failed?.toLocaleString() || 0}
                                    </div>
                                </div>

                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="text-purple-600" size={20} />
                                        <span className="font-medium text-slate-700">Retired</span>
                                    </div>
                                    <div className="text-2xl font-bold text-purple-700">
                                        {stats.scrape_stats.jobs_retired?.toLocaleString() || 0}
                                    </div>
                                </div>

                                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Database className="text-slate-600" size={20} />
                                        <span className="font-medium text-slate-700">Total Extracts</span>
                                    </div>
                                    <div className="text-2xl font-bold text-slate-700">
                                        {stats.scrape_stats.total_scrapes?.toLocaleString() || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Scraping Activity Chart */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                Extracting Activity ({timeframeLabel})
                            </h2>
                        </div>
                        <ActivityChart data={scrapeHistory} timeframe={timeframe} />
                    </div>

                    {/* Company Failures Grid */}
                    <ExtractStatusGrid failures={stats.company_failures && stats.company_failures} successes={stats.company_successes && stats.company_successes} timeframeLabel={timeframeLabel} />

                    {/* Top Employers */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Top 10 Employers by Job Count</h2>
                        <div className="space-y-3">
                            {stats.top_employers?.map((employer, index) => (
                                <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                                            {index + 1}
                                        </div>
                                        <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{employer.Name}</span>
                                    </div>
                                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                                        {employer.count} jobs
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}